import { useState } from 'react';
import { Form, Input, Select, Switch, Button, Space, Card } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { ChecklistItem, ChecklistItemType } from '@shared/types/work-order-template.types';

const { TextArea } = Input;

interface ChecklistItemFormProps {
  items: ChecklistItem[];
  onChange: (items: ChecklistItem[]) => void;
  disabled?: boolean;
}

const CHECKLIST_ITEM_TYPES: Array<{ label: string; value: ChecklistItemType }> = [
  { label: 'チェックボックス', value: 'check' },
  { label: 'テキスト', value: 'text' },
  { label: '数値', value: 'number' },
  { label: '日付', value: 'date' },
  { label: '選択肢', value: 'select' },
];

export const ChecklistItemForm: React.FC<ChecklistItemFormProps> = ({
  items,
  onChange,
  disabled = false,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form] = Form.useForm();

  const handleAdd = () => {
    form.resetFields();
    form.setFieldsValue({
      type: 'check',
      required: true,
    });
    setEditingIndex(items.length);
  };

  const handleEdit = (index: number) => {
    const item = items[index];
    form.setFieldsValue({
      title: item.title,
      description: item.description || '',
      required: item.required,
      type: item.type,
      options: item.options?.join('\n') || '',
      defaultValue: item.defaultValue,
    });
    setEditingIndex(index);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const newItem: ChecklistItem = {
        id: editingIndex !== null && editingIndex < items.length
          ? items[editingIndex].id
          : crypto.randomUUID(),
        order: editingIndex !== null && editingIndex < items.length
          ? items[editingIndex].order
          : items.length,
        title: values.title,
        description: values.description || undefined,
        required: values.required || false,
        type: values.type,
        options: values.type === 'select' && values.options
          ? values.options.split('\n').filter((opt: string) => opt.trim())
          : undefined,
        defaultValue: values.defaultValue || undefined,
      };

      let updatedItems: ChecklistItem[];
      if (editingIndex !== null && editingIndex < items.length) {
        updatedItems = [...items];
        updatedItems[editingIndex] = newItem;
      } else {
        updatedItems = [...items, newItem];
      }

      onChange(updatedItems);
      setEditingIndex(null);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleDelete = (index: number) => {
    const updatedItems = items
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, order: i }));
    onChange(updatedItems);
    if (editingIndex === index) {
      setEditingIndex(null);
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setEditingIndex(null);
    form.resetFields();
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updatedItems = [...items];
    [updatedItems[index - 1], updatedItems[index]] = [updatedItems[index], updatedItems[index - 1]];
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    onChange(updatedItems);
  };

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return;
    const updatedItems = [...items];
    [updatedItems[index], updatedItems[index + 1]] = [updatedItems[index + 1], updatedItems[index]];
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    onChange(updatedItems);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 16 }}>
        {items.map((item, index) => (
          <Card
            key={item.id}
            size="small"
            style={{ marginBottom: 8 }}
            title={
              <Space>
                <span>#{index + 1}</span>
                <span>{item.title}</span>
                {item.required && <span style={{ color: '#ff4d4f' }}>必須</span>}
                <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
                  ({CHECKLIST_ITEM_TYPES.find(t => t.value === item.type)?.label})
                </span>
              </Space>
            }
            extra={
              !disabled && (
                <Space>
                  <Button
                    size="small"
                    icon={<span>↑</span>}
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                  />
                  <Button
                    size="small"
                    icon={<span>↓</span>}
                    onClick={() => handleMoveDown(index)}
                    disabled={index === items.length - 1}
                  />
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(index)}
                  />
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(index)}
                  />
                </Space>
              )
            }
          >
            {item.description && (
              <div style={{ color: '#8c8c8c', fontSize: '12px', marginBottom: 4 }}>
                {item.description}
              </div>
            )}
            {item.type === 'select' && item.options && (
              <div style={{ fontSize: '12px', color: '#595959' }}>
                選択肢: {item.options.join(', ')}
              </div>
            )}
          </Card>
        ))}
      </div>

      {editingIndex !== null && (
        <Card title={editingIndex < items.length ? '項目を編集' : '項目を追加'} style={{ marginBottom: 16 }}>
          <Form
            form={form}
            layout="vertical"
            component="div"
            initialValues={{
              type: 'check',
              required: true,
            }}
          >
            <Form.Item
              label="項目名"
              name="title"
              rules={[{ required: true, message: '項目名を入力してください' }]}
            >
              <Input placeholder="例: 安全確認" />
            </Form.Item>

            <Form.Item label="説明" name="description">
              <TextArea rows={2} placeholder="例: 作業前に安全確認を実施する" />
            </Form.Item>

            <Form.Item label="項目タイプ" name="type" rules={[{ required: true }]}>
              <Select options={CHECKLIST_ITEM_TYPES} placeholder="タイプを選択" />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.type !== curr.type}>
              {() => {
                const type = form.getFieldValue('type');
                if (type === 'select') {
                  return (
                    <Form.Item
                      label="選択肢（1行に1つ）"
                      name="options"
                      rules={[{ required: true, message: '選択肢を入力してください' }]}
                    >
                      <TextArea rows={3} placeholder="良好&#10;異常あり&#10;要修理" />
                    </Form.Item>
                  );
                }
                return null;
              }}
            </Form.Item>

            <Form.Item label="必須項目" name="required" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" onClick={handleSave}>
                  保存
                </Button>
                <Button onClick={handleCancel}>キャンセル</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      )}

      {!disabled && editingIndex === null && (
        <Button type="dashed" onClick={handleAdd} block icon={<PlusOutlined />}>
          チェックリスト項目を追加
        </Button>
      )}
    </div>
  );
};
